import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { parseIconValue, toCuratedIconValue } from "components/Icon/iconValue";
import { useMemo, useState } from "react";
import "./IconPicker.scss";

export interface IconPickerProps {
    /** The record's current stored `icon` value (e.g. `mdi:<key>` or a `data:` URI). */
    value?: string | null;
    /** Called with the `mdi:<key>` stored form when the user picks a curated icon. */
    onSelect: (curatedValue: string) => void;
}

// Turn a camelCase IconCollection key ("monitorDashboard") into space-separated
// words ("monitor dashboard") so a search like "dash" or "monitor dash" matches.
export const humanizeIconKey = (key: string): string =>
    key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();

// Filter the curated icon keys by a free-text query, matching either the raw
// camelCase key or its humanized (space-separated) form, case-insensitively.
export const filterIconKeys = (keys: string[], query: string): string[] => {
    const q = query.trim().toLowerCase();
    if (!q) {
        return keys;
    }
    return keys.filter(
        (key) => key.toLowerCase().includes(q) || humanizeIconKey(key).includes(q)
    );
};

// A searchable grid of the curated built-in icons (IconCollection). Selecting a
// glyph emits its `mdi:<key>` stored form via onSelect. Reusable across every
// resource whose icon field supports curated values.
export const IconPicker = ({ value, onSelect }: IconPickerProps) => {
    const [query, setQuery] = useState("");

    const parsed = parseIconValue(value);
    const selectedKey = parsed.kind === "curated" ? parsed.key : undefined;

    const allKeys = useMemo(() => Object.keys(IconCollection).sort(), []);
    const keys = useMemo(() => filterIconKeys(allKeys, query), [allKeys, query]);

    return (
        <div className="icon-picker">
            <input
                className="icon-picker__search"
                type="text"
                autoFocus
                placeholder="Search icons…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search icons"
            />
            {keys.length === 0 ? (
                <div className="icon-picker__empty">No icons match “{query.trim()}”.</div>
            ) : (
                <div className="icon-picker__grid" role="listbox" aria-label="Curated icons">
                    {keys.map((key) => (
                        <button
                            key={key}
                            type="button"
                            role="option"
                            title={humanizeIconKey(key)}
                            aria-label={key}
                            aria-selected={key === selectedKey}
                            className={`icon-picker__item${
                                key === selectedKey ? " icon-picker__item--selected" : ""
                            }`}
                            onClick={() => onSelect(toCuratedIconValue(key))}
                        >
                            <Icon icon={IconCollection[key]} className="icon-picker__glyph" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
